// src/stores/favoritesStore.js
import { defineStore } from 'pinia';
import axios from 'axios';

const api = axios.create({
    baseURL: 'https://eisenbahn.wingen.de/api/user',
    headers: { 'Content-Type': 'application/json' },
});

let getToken = null;
export function bindFavoritesTokenGetter(fn) { getToken = fn; }

api.interceptors.request.use((config) => {
    const token = getToken && getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export const useFavoritesStore = defineStore('favorites', {
    state: () => ({
        folders: [],
        currentImageFolders: [], // Which folders contain the current image
        loading: false,
        error: null,
    }),

    getters: {
        isImageInAnyFolder: (state) => state.currentImageFolders.length > 0,
        isImageInFolder: (state) => (folderId) => {
            return state.currentImageFolders.some(f => f.folder_id === folderId);
        },
    },

    actions: {
        // Fetch all folders
        async fetchFolders() {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.get('/favorites/folders');
                if (data.ok) {
                    this.folders = data.folders;
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Laden der Ordner';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Create new folder
        async createFolder(name) {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.post('/favorites/folders', { name });
                if (data.ok && data.folder) {
                    this.folders.push(data.folder);
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Erstellen des Ordners';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Rename folder
        async renameFolder(folderId, name) {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.put('/favorites/folder', { folder_id: folderId, name });
                if (data.ok) {
                    const folder = this.folders.find(f => f.id === folderId);
                    if (folder) folder.name = name;
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Umbenennen';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Delete folder
        async deleteFolder(folderId) {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.delete('/favorites/folder', {
                    data: { folder_id: folderId }
                });
                if (data.ok) {
                    this.folders = this.folders.filter(f => f.id !== folderId);
                    this.currentImageFolders = this.currentImageFolders.filter(f => f.folder_id !== folderId);
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Löschen';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Check which folders contain an image
        async checkImageFolders(imageId) {
            try {
                const { data } = await api.get(`/favorites?image_id=${imageId}`);
                if (data.ok) {
                    this.currentImageFolders = data.in_folders || [];
                }
                return data;
            } catch (err) {
                this.currentImageFolders = [];
                return { ok: false };
            }
        },

        // Add image to folder
        async addToFolder(folderId, imageId) {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.post('/favorites', { folder_id: folderId, image_id: imageId });
                if (data.ok) {
                    // Update local state
                    const folder = this.folders.find(f => f.id === folderId);
                    if (folder) {
                        folder.favorite_count = (folder.favorite_count || 0) + 1;
                    }
                    // Add to current image folders if not already there
                    if (!this.currentImageFolders.some(f => f.folder_id === folderId)) {
                        this.currentImageFolders.push({
                            folder_id: folderId,
                            folder_name: folder?.name
                        });
                    }
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Hinzufügen';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Remove image from folder
        async removeFromFolder(folderId, imageId) {
            this.loading = true;
            this.error = null;
            try {
                const { data } = await api.delete('/favorites', {
                    data: { folder_id: folderId, image_id: imageId }
                });
                if (data.ok) {
                    // Update local state
                    const folder = this.folders.find(f => f.id === folderId);
                    if (folder && folder.favorite_count > 0) {
                        folder.favorite_count--;
                    }
                    this.currentImageFolders = this.currentImageFolders.filter(f => f.folder_id !== folderId);
                }
                return data;
            } catch (err) {
                this.error = err?.response?.data?.error || 'Fehler beim Entfernen';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Move image to another folder
        async moveToFolder(fromFolderId, toFolderId, imageId) {
            this.loading = true;
            this.error = null;
            try {
                // First add to new folder
                const addResult = await api.post('/favorites', { folder_id: toFolderId, image_id: imageId });
                if (!addResult.data.ok && !addResult.data.already_exists) {
                    throw new Error(addResult.data.error || 'Fehler beim Hinzufügen');
                }

                // Then remove from old folder
                const removeResult = await api.delete('/favorites', {
                    data: { folder_id: fromFolderId, image_id: imageId }
                });

                if (removeResult.data.ok) {
                    // Update local state
                    const fromFolder = this.folders.find(f => f.id === fromFolderId);
                    const toFolder = this.folders.find(f => f.id === toFolderId);
                    if (fromFolder && fromFolder.favorite_count > 0) {
                        fromFolder.favorite_count--;
                    }
                    if (toFolder) {
                        toFolder.favorite_count = (toFolder.favorite_count || 0) + 1;
                    }
                }

                return { ok: true };
            } catch (err) {
                this.error = err?.response?.data?.error || err.message || 'Fehler beim Verschieben';
                return { ok: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        // Get images in a folder
        async getFolderImages(folderId) {
            try {
                const { data } = await api.get(`/favorites?folder_id=${folderId}`);
                if (data.ok) {
                    return data.favorites || [];
                }
                return [];
            } catch (err) {
                return [];
            }
        },

        // Reset state (on logout)
        reset() {
            this.folders = [];
            this.currentImageFolders = [];
            this.loading = false;
            this.error = null;
        },
    },
});
